import { useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { invalidateQuery, invalidateQueryPrefix } from "./useQueryCore";
import { syncOfflineData } from "../lib/api";

// Subscribes to Supabase realtime changes for all 5 materials tables scoped to
// the current district. When a remote user modifies any record, the relevant
// cached queries are invalidated so all active views refresh automatically.
//
// Anti-loop guarantee: Supabase only broadcasts rows that committed at the DB
// level. Our own writes (via executeMutation) already handle cache invalidation
// synchronously, so the realtime event for our own writes causes one extra
// invalidation at most — not a recursive loop.
//
// Call once at app level (e.g. inside the authenticated shell) so the
// subscription lives as long as the user session.
export function useMaterialsRealtime(districtId) {
  const channelRef = useRef(null);

  useEffect(() => {
    if (!districtId) return;

    const channelName = `materials:${districtId}`;

    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "church_material_allocations",
        filter: `district_id=eq.${districtId}`,
      }, (payload) => {
        invalidateQueryPrefix(["church-allocations", districtId]);
        // Also invalidate detail if we know the ID
        const id = payload.new?.id || payload.old?.id;
        if (id) invalidateQuery(["allocation-detail", id]);
      })
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "church_material_payments",
        filter: `district_id=eq.${districtId}`,
      }, (payload) => {
        const allocId = payload.new?.allocation_id || payload.old?.allocation_id;
        if (allocId) invalidateQuery(["allocation-detail", allocId]);
        invalidateQueryPrefix(["church-allocations", districtId]);
      })
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "subscription_deliveries",
        filter: `district_id=eq.${districtId}`,
      }, (payload) => {
        const subId = payload.new?.subscription_id || payload.old?.subscription_id;
        if (subId) invalidateQuery(["subscription-deliveries", subId]);
      })
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "church_subscriptions",
        filter: `district_id=eq.${districtId}`,
      }, (payload) => {
        invalidateQueryPrefix(["church-subscriptions", districtId]);
        const id = payload.new?.id || payload.old?.id;
        if (id) invalidateQuery(["subscription-detail", id]);
      })
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "material_stock",
        filter: `district_id=eq.${districtId}`,
      }, () => {
        invalidateQueryPrefix(["material-stock", districtId]);
        invalidateQueryPrefix(["materials-list", districtId]);
      })
      .subscribe((status, err) => {
        // CHANNEL_ERROR / TIMED_OUT means the WebSocket dropped — realtime events stop arriving.
        // Supabase reconnects automatically; log so we can detect persistent failures in dev.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[realtime] materials channel error', { channel: channelName, status, error: err?.message });
        }
      });

    channelRef.current = channel;

    // Force-resubscribe when network comes back if the channel dropped
    const handleOnline = () => {
      const ch = channelRef.current;
      if (ch && ch.state !== 'joined') {
        ch.subscribe();
      }
      // Flush any outbox items that accumulated while offline
      syncOfflineData().catch(() => {});
    };
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
      channel.unsubscribe();
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [districtId]);
}
